import { useInfiniteQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const InfiniteScroll = forwardRef(
  (
    {
      fetchFn,
      queryKey,
      renderItem,
      loadingIndicator,
      loadingMoreIndicator,
      emptyIndicator,
      containerClassName,
      reverse = false,
      initialPages = 1,
      enabled = true,
    },
    ref
  ) => {
    const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
      useInfiniteQuery({
        queryKey,
        queryFn: fetchFn,
        initialPageParam: 0,
        getNextPageParam: (lastPage) =>
          lastPage.last ? undefined : lastPage.number + 1,
        staleTime: 5 * 60 * 1000,
        enabled,
      });

    const scrollContainer = useRef(null);
    const scrollPosition = useRef(0);

    // Expose scroll methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom: () => {
          if (scrollContainer.current) {
            scrollContainer.current.scrollTop =
              scrollContainer.current.scrollHeight;
          }
        },
        scrollToTop: () => {
          if (scrollContainer.current) {
            scrollContainer.current.scrollTop = 0;
          }
        },
        getScrollElement: () => scrollContainer.current,
        isAtBottom: () => {
          if (!scrollContainer.current) return false;
          const { scrollTop, scrollHeight, clientHeight } =
            scrollContainer.current;
          return scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
        },
      }),
      []
    );

    useEffect(() => {
      if (reverse && scrollContainer.current) {
        if (isFetchingNextPage) {
          scrollPosition.current =
            scrollContainer.current.scrollHeight -
            scrollContainer.current.scrollTop;
        } else {
          scrollContainer.current.scrollTop =
            scrollContainer.current.scrollHeight - scrollPosition.current;
        }
      }
    }, [data, isFetchingNextPage, reverse]);

    // If the loaded content doesn't fill the visible area yet, there's
    // nothing to scroll — so the scroll-triggered fetch below would never
    // fire. Keep pulling pages until the viewport is full or data runs out.
    useEffect(() => {
      const el = scrollContainer.current;
      if (!el || isLoading || isFetchingNextPage || !hasNextPage) return;
      if (el.scrollHeight <= el.clientHeight) {
        fetchNextPage();
      }
    }, [data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

    useEffect(() => {
      const fetchInitialPages = async () => {
        for (let i = 1; i < initialPages; i++) {
          if (hasNextPage) {
            await fetchNextPage();
          }
        }
      };

      if (enabled && initialPages > 1) {
        fetchInitialPages();
      }
    }, [initialPages, fetchNextPage, hasNextPage]);

    // Auto-fetch next page if the container doesn't have a scrollbar yet
    // This allows InfiniteScroll to work even if the initial PAGE_SIZE is too small
    useEffect(() => {
      if (!scrollContainer.current || !hasNextPage || isFetchingNextPage || isLoading) return;

      const { scrollHeight, clientHeight } = scrollContainer.current;
      if (scrollHeight <= clientHeight && scrollHeight > 0) {
        fetchNextPage();
      }
    }, [data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);


    const handleScroll = (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const threshold = 100; // how close to bottom/top to trigger load

      // Normal scroll (load more when near bottom)
      if (!reverse && scrollHeight - scrollTop - clientHeight < threshold) {
        if (!isFetchingNextPage && hasNextPage) fetchNextPage();
      }

      // Reverse scroll (load more when near top)
      if (reverse && scrollTop < threshold) {
        if (!isFetchingNextPage && hasNextPage) fetchNextPage();
      }
    };

    const isEmpty =
      !isLoading && data?.pages?.[0]?.content?.length === 0;

    return (
      <div
        ref={scrollContainer}
        className={`${containerClassName} relative h-full overflow-y-auto`}
        onScroll={handleScroll}
      >
        {isLoading && loadingIndicator}

        {reverse && isFetchingNextPage && loadingMoreIndicator}

        {reverse &&
          data?.pages
            ?.slice()
            .reverse()
            .map((page) =>
              page.content
                .slice()
                .reverse()
                .map((item) => renderItem(item))
            )}
        {!reverse &&
          data?.pages?.map((page) =>
            page.content.map((item) => renderItem(item))
          )}

        {!reverse && isFetchingNextPage && loadingMoreIndicator}

        {!isLoading &&
          !isEmpty &&
          data?.pages?.[data.pages.length - 1]?.last &&
          emptyIndicator}

        {isEmpty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h3
              className="mt-3 font-semibold text-gray-600
                 text-sm
                 lg:text-base
                 xl:text-lg
                 2xl:text-xl"
            >
              No Data Found
            </h3>

            <p
              className="mt-1 text-gray-400
                text-xs
                lg:text-sm
                xl:text-base"
            >
              Data will appear here once records are available
            </p>
          </div>
        )}
      </div>
    );
  }
);

InfiniteScroll.displayName = 'InfiniteScroll';

InfiniteScroll.propTypes = {
  fetchFn: PropTypes.func.isRequired,
  queryKey: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  loadingIndicator: PropTypes.node.isRequired,
  loadingMoreIndicator: PropTypes.node,
  emptyIndicator: PropTypes.node.isRequired,
  containerClassName: PropTypes.string,
  reverse: PropTypes.bool,
  initialPages: PropTypes.number,
  enabled: PropTypes.bool,
};

export default InfiniteScroll;
